import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { accompanyShoppingAfterSaleApi } from '../../api/accompanyShoppingAfterSale';

// 售后服务类型常量
export const AFTER_SALE_TYPE = {
  REFUND: 1,
  SERVICE_COMPENSATION: 2,
  COMPLAINT: 3
};

// 售后服务状态常量
export const AFTER_SALE_STATUS = {
  PENDING: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  REJECTED: 3
};

// 生成模拟陪逛服务售后数据的函数
const generateMockAfterSales = (total = 50) => {
  const afterSaleTypes = [1, 2, 3];
  const afterSaleStatuses = [0, 1, 2, 3];
  const evidenceImages = [
    'https://picsum.photos/seed/1/200/200',
    'https://picsum.photos/seed/2/200/200',
    'https://picsum.photos/seed/3/200/200'
  ];

  return Array.from({ length: total }, (_, index) => ({
    id: index + 1,
    order_id: Math.floor(Math.random() * 500) + 1,
    user_id: Math.floor(Math.random() * 1000) + 1,
    provider_id: Math.floor(Math.random() * 100) + 1,
    type: afterSaleTypes[Math.floor(Math.random() * afterSaleTypes.length)],
    reason: `售后原因 ${index + 1}：服务不满意`,
    description: `详细说明 ${index + 1}：服务未达到预期`,
    evidence_images: JSON.stringify(
      evidenceImages.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 3) + 1)
    ),
    refund_amount: Math.random() > 0.5 ? parseFloat((Math.random() * 500).toFixed(2)) : null,
    status: afterSaleStatuses[Math.floor(Math.random() * afterSaleStatuses.length)],
    handler_id: Math.random() > 0.5 ? Math.floor(Math.random() * 50) + 1 : null,
    handler_response: Math.random() > 0.5 ? `处理回复 ${index + 1}` : null,
    create_time: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    update_time: new Date().toISOString()
  }));
};

// 获取售后服务列表
export const fetchAccompanyShoppingAfterSaleList = createAsyncThunk(
  'accompanyShoppingAfterSale/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await accompanyShoppingAfterSaleApi.getAfterSaleList(params);
      return response.data;
    } catch (error) {
      console.error('获取陪逛服务售后列表失败:', error);
      
      // 返回模拟数据
      const mockData = {
        list: generateMockAfterSales(),
        total: 50,
        current: params.current || 1,
        pageSize: params.pageSize || 10
      };
      
      return rejectWithValue({
        message: error.message || '获取陪逛服务售后列表失败',
        mockData
      });
    }
  }
);

// 处理售后服务
export const handleAfterSale = createAsyncThunk(
  'accompanyShoppingAfterSale/handle',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await accompanyShoppingAfterSaleApi.handleAfterSale(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新售后服务状态
export const updateAfterSaleStatus = createAsyncThunk(
  'accompanyShoppingAfterSale/updateStatus',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await accompanyShoppingAfterSaleApi.updateAfterSaleStatus(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 拒绝售后服务
export const rejectAfterSale = createAsyncThunk(
  'accompanyShoppingAfterSale/reject',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await accompanyShoppingAfterSaleApi.rejectAfterSale(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 完成售后服务
export const completeAfterSale = createAsyncThunk(
  'accompanyShoppingAfterSale/complete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await accompanyShoppingAfterSaleApi.completeAfterSale(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 陪逛服务售后 Slice
const accompanyShoppingAfterSaleSlice = createSlice({
  name: 'accompanyShoppingAfterSale',
  initialState: {
    list: generateMockAfterSales(10), // 初始化时使用模拟数据
    currentAfterSale: null,
    loading: false,
    error: null,
    pagination: {
      total: 50,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchAccompanyShoppingAfterSaleList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccompanyShoppingAfterSaleList.fulfilled, (state, action) => {
        state.loading = false;
        // 处理新的数据结构：data.records, data.total, data.current, data.size
        const data = action.payload.data || action.payload;
        state.list = data.records || data.list || [];
        state.pagination.total = data.total || 0;
        state.pagination.current = data.current || 1;
        state.pagination.pageSize = data.size || data.pageSize || 10;
      })
      .addCase(fetchAccompanyShoppingAfterSaleList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        
        // 如果有模拟数据，使用模拟数据
        if (action.payload && action.payload.mockData) {
          state.list = action.payload.mockData.list;
          state.pagination.total = action.payload.mockData.total;
        }
      })
      
      // 处理售后服务
      .addCase(handleAfterSale.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      
      // 更新状态
      .addCase(updateAfterSaleStatus.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      
      // 拒绝售后服务
      .addCase(rejectAfterSale.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      
      // 完成售后服务
      .addCase(completeAfterSale.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      });
  }
});

export const { setPagination } = accompanyShoppingAfterSaleSlice.actions;
export default accompanyShoppingAfterSaleSlice.reducer; 